/* Derived view-model for the login route.
 * Ported from the artifact's vals_login(). The form gates a browser-only demo:
 * there is no auth endpoint, and nothing is transmitted. */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionRows } from '@/data/wft';
import { setSignedIn } from '@/lib/prefs';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 6;

export function useLoginVals(now: Date | null) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The artifact left this timeout running across unmount, which navigated a
  // torn-down tree and warned in the console.
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const submit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Guard added: the original had no busy check, so a double-submit fired
      // two navigations.
      if (busy) return;

      const trimmed = email.trim();
      if (!EMAIL_RE.test(trimmed)) {
        setError('Enter an email address in the form name@domain.tld.');
        return;
      }
      if (password.length < MIN_PASSWORD) {
        setError('Password must be at least six characters. Nothing is transmitted.');
        return;
      }

      setError('');
      setBusy(true);
      setSignedIn(true);
      timer.current = setTimeout(() => {
        // Into the product, not the page describing it. A full navigation
        // because /cockpit belongs to the terminal app, not this router.
        window.location.assign('/cockpit');
        setBusy(false);
      }, 250);
    },
    [busy, email, password, navigate]
  );

  return {
    sessions: sessionRows(now),
    chips: ['20 sections', '17 connectors', 'TimesFM 2.5', 'swarm resolve', 'keyboard-first'],
    email,
    password,
    busy,
    hasError: !!error,
    errorText: error,
    buttonLabel: busy ? 'Opening terminal…' : 'Sign in',
    onEmail: (e: FormEvent<HTMLInputElement>) => {
      setEmail(e.currentTarget.value);
      setError('');
    },
    onPassword: (e: FormEvent<HTMLInputElement>) => {
      setPassword(e.currentTarget.value);
      setError('');
    },
    submit,
  };
}

export type LoginVals = ReturnType<typeof useLoginVals>;
